import { defineStore } from 'pinia';

export const useEpisodeManageStore = defineStore('episodeManage', {
  state: () => ({
    episode: {
      uuid: null,
      name: null,
      description: null,
      tags: [],
      artwork: null,
      artwork_uuid: null,
      downloadable: window.preferences?.default_download ?? true,
      published: true,
      publish_start: null,
      publish_stop: null,
      storage: null,
      description_incl_chapters: true,
      episode_detail: {
        type: 'full',
        season_number: null,
        episode_number: null,
        explicit: 'no',
        author: null,
        link: null,
      },
      people: [],
    },
    podcast: null,
    auto_complete_tags: [],
    ms_tag_values: [],
    episode_explicit_options: [
      {
        value: 'yes',
        display: 'Yes',
      },
      {
        value: 'clean',
        display: 'Clean',
      },
    ],
    episode_type_options: [
      {
        value: 'full',
        display: 'Full Episode',
        description: 'This is a standard full-length episode of your podcast.',
      },
      {
        value: 'trailer',
        display: 'Trailer',
        description: 'A short, promotional piece of content that represents a preview of your current podcast.',
      },
      {
        value: 'bonus',
        display: 'Bonus',
        description: 'Extra content for your podcast (for example, behind-the-scenes information or interviews with the cast) or cross-promotional content for another podcast.',
      },
    ],
    saving: false,
  }),
  actions: {
    setEpisode(the_episode) {
      // In the event an episode was created from a broadcaster it might not have episode_detail.
      if (!the_episode.episode_detail) {
        the_episode.episode_detail = { ...this.episode.episode_detail };
      }

      this.episode = the_episode;

      this.setEpisodeExplicitOption();
      this.setTypeOption();
      this.setupEpisode();
    },
    setupEpisode() {
      this.ms_tag_values = this.episode.tags.map(tag => tag.name);
    },
    setArtwork(artwork) {
      this.episode.artwork = artwork;
      this.episode.artwork_uuid = artwork.uuid;
    },
    setStorageAsset(storage) {
      this.episode.storage = storage;
    },
    setEpisodeExplicitOption() {
      if (!this.episode.episode_detail?.explicit) {
        this.episode_explicit_options[0].checked = true;
        return;
      }

      // Hack because we removed 'no' from options
      if (this.episode.episode_detail.explicit === 'no') {
        this.episode.episode_detail.explicit = 'clean';
      }

      const explicit_option = _.find(this.episode_explicit_options, {
        'value': this.episode.episode_detail.explicit,
      });
      explicit_option.checked = true;
    },
    setTypeOption() {
      if (!this.episode.episode_detail?.type) {
        this.episode_type_options[0].checked = true;
        return;
      }
      const type_option = _.find(this.episode_type_options, {
        'value': this.episode.episode_detail.type,
      });
      type_option.checked = true;
    },
    setPodcast(podcast) {
      this.podcast = podcast;
      if (!this.podcast.itunes_detail) {
        toastr.error("Something is wrong with your Podcast. We cannot add an episode at this time. Please check the Podcast and try again.");
        window.location.href = `/feeds/${podcast.uuid}/edit`;
      }
    },
    async validateAndSave(validator, update = false, redirect = false) {
      this.saving = true;
      // Validate all the forms individually
      const forms_valid = await this.validateForms(validator);
      if (!forms_valid) {
        this.saving = false;
        return;
      }

      // Create non-reactive copy of episode. Trust me, has to be done this way.
      const data = JSON.parse(JSON.stringify(this.episode));
      // Make amendments required for patch method.
      data.tags = this.ms_tag_values;
      data.storage_uuid = data.storage.uuid;

      if (!update) {
        delete this.episode.people;
      }

      // If we are editing the episode we don't need the feed_uuid.
      if (this.podcast) {
        data.feed_uuid = this.podcast;
      }

      let api_response = null;

      try {
        if (update) {
          api_response = await axios.patch(`/api/v1/episodes/${this.episode.uuid}`, data);
        } else {
          api_response = await axios.post('/api/v1/episodes', data);
        }
        EventBus.$emit(update ? 'episode_updated' : 'episode_added');
        EventBus.$emit('modal_close_item-create-modal');
        this.saving = false;
        toastr.success(update ? 'Episode Updated' : 'Episode Created');
      } catch (error) {
        window.handleAxiosError(error);
        this.saving = false;
        return;
      }

      if (redirect) {
        window.location.href = `/episode/${api_response.data.data.uuid}/edit?feed_uuid=${this.podcast?.uuid}`;
      }
    },
    async validateForms(validator) {
      const general_form_valid = await validator.validateAll('episode-general-form');
      if (!general_form_valid) {
        toastr.error(`Some fields in the 'General' section have not been populated correctly. Please fix and try again.`);
      }

      const publishing_form_valid = await validator.validateAll('episode-publishing-form');
      if (!publishing_form_valid) {
        toastr.error(`Some fields in the 'Publishing' section have not been populated correctly. Please fix and try again.`);
      }

      const advanced_form_valid = await validator.validateAll('episode-advanced-form');
      if (!advanced_form_valid) {
        toastr.error(`Some fields in the 'Advanced Settings' section have not been populated correctly. Please fix and try again.`);
      }

      return general_form_valid && publishing_form_valid && advanced_form_valid;
    },
    async getAllTags() {
      try {
        this.auto_complete_tags = (await axios.get(`/api/v1/tags?per_page=1000`)).data.data?.map(tag => tag.name);
      } catch (error) {
        window.handleAxiosError(error);
      }
    },
    addTag(tag) {
      tag = tag.toLowerCase();
      this.auto_complete_tags.push(tag);
      this.ms_tag_values.push(tag);
    },
  },
});
